import { useState } from 'react'
import { useTranslation } from 'react-i18next'
import DOMPurify from 'dompurify'
import {
  Dialog,
  DialogContent,
} from '@/components/ui/dialog'
import { PenLine, Star } from 'lucide-react'
import { Button } from '@/components/ui/button'
import { useSequenceStepsPreview } from '@/hooks/useApi'
import { SIGNATURE_SEPARATOR_HTML } from '@/lib/email'
import { SAMPLE_CONTACT_EMAIL } from '@/lib/templateVariables'
import type { Contact } from '@/types/api'
import { MissingVariablesNotice } from './MissingVariablesNotice'
import { RenderContactSelect } from './RenderContactSelect'

interface StepPreviewProps {
  open: boolean
  onOpenChange: (open: boolean) => void
  sequenceId: string
  stepId: string
  signatureHtml?: string
}

interface StepPreviewContentProps {
  sequenceId: string
  stepId: string
  signatureHtml?: string
  contact: Contact | null
  onContactChange: (contact: Contact | null) => void
}

/**
 * Anteprima di uno step come arriverà al destinatario. Il contenuto è sempre
 * renderizzato dal server, anche con i dati di esempio, con lo stesso render
 * dell'invio di test e dell'invio reale: token, oggetto dei follow-up (#264)
 * e override dell'enrollment del contatto (#265).
 */
export function StepPreview({
  open,
  onOpenChange,
  sequenceId,
  stepId,
  signatureHtml,
}: StepPreviewProps) {
  const [contact, setContact] = useState<Contact | null>(null)

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="max-w-2xl p-0 overflow-hidden">
        {/* Montato solo ad anteprima aperta: ogni apertura rilegge il server */}
        <StepPreviewContent
          sequenceId={sequenceId}
          stepId={stepId}
          signatureHtml={signatureHtml}
          contact={contact}
          onContactChange={setContact}
        />
      </DialogContent>
    </Dialog>
  )
}

function StepPreviewContent({
  sequenceId,
  stepId,
  signatureHtml,
  contact,
  onContactChange,
}: StepPreviewContentProps) {
  const { t } = useTranslation('sequences')
  const preview = useSequenceStepsPreview(sequenceId, contact?.id ?? null)
  const serverStep = preview.data?.steps.find((s) => s.step_id === stepId)

  // Senza lo step nella risposta non si resta in caricamento: o la richiesta
  // è fallita o lo step non esiste più nella sequenza salvata
  const bodyStatus = serverStep
    ? null
    : preview.isError
      ? t('common:contactPreview.error')
      : preview.isPending
        ? t('common:contactPreview.loading')
        : t('preview.stepNotFound')

  const recipientName = contact
    ? contact.full_name || contact.email || t('preview.fallbackRecipient')
    : t('preview.sampleRecipientName')
  const recipientEmail = contact ? contact.email : SAMPLE_CONTACT_EMAIL

  // Dal secondo step l'email parte con le precedenti citate sotto (#334): il
  // testo citato esiste solo all'invio, qui se ne segnala la presenza
  const quotesPreviousEmails =
    serverStep !== undefined &&
    serverStep.step_order > 1 &&
    preview.data?.quote_previous_emails === true

  const renderedBody = serverStep?.body_html ?? ''
  const fullBody = signatureHtml
    ? `${renderedBody}${SIGNATURE_SEPARATOR_HTML}${signatureHtml}`
    : renderedBody

  return (
    <>
      {/* Contact selector */}
      <div className="pl-4 pr-12 pt-3 pb-1">
        <RenderContactSelect value={contact} onChange={onContactChange} />
      </div>

      {/* Email client chrome */}
      <div className="bg-zinc-100 dark:bg-zinc-900 border-b px-4 py-3">
        {serverStep ? (
          <p className="font-semibold text-base">{serverStep.subject}</p>
        ) : (
          <p className="min-h-[1.5rem] font-semibold text-base text-muted-foreground">
            {!preview.isPending && t('preview.subjectUnavailable')}
          </p>
        )}
      </div>

      {/* Header bar */}
      <div className="px-4 py-3 border-b flex items-start gap-3">
        <div className="w-10 h-10 rounded-full bg-blue-500 text-white flex items-center justify-center font-bold text-sm shrink-0 mt-0.5">
          {recipientName.charAt(0).toUpperCase()}
        </div>
        <div className="flex-1 min-w-0">
          <div className="flex items-center gap-2">
            {/* eslint-disable-next-line i18next/no-literal-string -- sample sender address in the email preview mockup, not UI copy */}
            <span className="font-medium text-sm">emanuele@exelab.com</span>
          </div>
          <p className="text-xs text-muted-foreground">
            {t('preview.to')} {recipientName} &lt;{recipientEmail}&gt;
          </p>
        </div>
        <div className="flex items-center gap-1 text-muted-foreground">
          <Star className="h-4 w-4" />
        </div>
      </div>

      {/* Email body */}
      <div className="px-6 py-5 max-h-[60vh] overflow-y-auto space-y-3">
        {serverStep?.has_override && (
          <p role="note" className="flex items-center gap-1.5 text-xs text-muted-foreground">
            <PenLine className="h-3.5 w-3.5 shrink-0" />
            {t('preview.overrideApplied')}
          </p>
        )}
        <MissingVariablesNotice variables={serverStep?.missing_variables ?? []} />
        {bodyStatus ? (
          <div className="flex items-center gap-3">
            <p className="text-sm text-muted-foreground">{bodyStatus}</p>
            {preview.isError && (
              <Button
                type="button"
                variant="outline"
                size="sm"
                onClick={() => void preview.refetch()}
              >
                {t('preview.retry')}
              </Button>
            )}
          </div>
        ) : (
          <div
            className="text-sm leading-relaxed"
            dangerouslySetInnerHTML={{ __html: DOMPurify.sanitize(fullBody) }}
          />
        )}
        {quotesPreviousEmails && (
          <p
            data-testid="quoted-emails-placeholder"
            className="border-l-2 border-muted-foreground/30 pl-3 text-xs italic text-muted-foreground"
          >
            {t('preview.quotedEmailsPlaceholder')}
          </p>
        )}
      </div>
    </>
  )
}
